import React from 'react';
import { Image, View, useWindowDimensions } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';

import { useKeyboardInset } from '../hooks/useKeyboardInset';
import { cn } from '../utils/cn';

interface AuthLayoutProps {
  children: React.ReactNode;
  testID?: string;
}

// 50dp base clearance kept below a focused field's caret when
// KeyboardAwareScrollView scrolls it above the keyboard. Sized to also clear
// the error/footnote text rendered below the outlined Input. Scaled by
// fontScale so it stays proportional at larger accessibility font sizes.
const SCROLL_TO_ERROR_BASE_OFFSET = 50;
const DESKTOP_BREAKPOINT = 768;

export const AuthLayout: React.FC<AuthLayoutProps> = ({ children, testID }) => {
  const { width, fontScale } = useWindowDimensions();
  const isDesktop = width >= DESKTOP_BREAKPOINT;
  const bottomOffset = Math.round(SCROLL_TO_ERROR_BASE_OFFSET * fontScale);
  const keyboardInset = useKeyboardInset();
  const basePadding = isDesktop ? 0 : 16;

  const renderIllustration = () => (
    <View style={{ flex: 1, backgroundColor: '#240059', justifyContent: 'center', alignItems: 'center', padding: 40 }}>
      <Image
        source={require('../assets/images/vida-login-img.png')}
        style={{ width: '90%', aspectRatio: 1905 / 2880 }}
        resizeMode='contain'
      />
    </View>
  );

  const form = (
    // Web-only quirk: KeyboardAwareScrollView drops its flex:1 in the desktop row without this outer wrapper. No-op on mobile.
    <View style={{ flex: 1 }}>
      <KeyboardAwareScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{
          flexGrow: 1,
          justifyContent: 'center',
          alignItems: 'center',
          paddingHorizontal: basePadding,
          paddingTop: basePadding,
          paddingBottom: basePadding + keyboardInset,
        }}
        keyboardShouldPersistTaps='handled'
        showsVerticalScrollIndicator={false}
        bottomOffset={bottomOffset}
      >
        <View
          className={cn(!isDesktop && 'w-full max-w-[440px] bg-login-surface-pure p-10')}
          style={isDesktop ? { width: '80%' } : undefined}
        >
          {children}
        </View>
      </KeyboardAwareScrollView>
    </View>
  );

  return (
    <View className='flex-1 bg-login-surface-pure' testID={testID}>
      {isDesktop ? (
        <View style={{ flexDirection: 'row', flex: 1 }}>
          {renderIllustration()}
          {form}
        </View>
      ) : (
        form
      )}
    </View>
  );
};
